import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useFieldControl } from './Field';

export type ControlSize = 'sm' | 'md' | 'lg';

export const CONTROL_SIZE: Record<ControlSize, string> = {
  sm: 'h-9 text-sm',
  md: 'h-11 text-base',
  lg: 'h-13 text-lg',
};

/** Shared shell so Input, Select and Textarea look like one family. */
export const CONTROL_BASE =
  'w-full rounded-md border border-border bg-surface-2 text-text placeholder:text-text-faint ' +
  'transition-colors duration-150 ease-out-soft ' +
  'hover:border-border-strong ' +
  'disabled:cursor-not-allowed disabled:opacity-50 ' +
  'aria-[invalid=true]:border-loss aria-[invalid=true]:bg-loss-dim/40';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Height preset. Named `inputSize` because `size` is a native input attribute. */
  inputSize?: ControlSize;
  icon?: LucideIcon;
  /** Rendered inside the field on the right (unit, clear button, toggle…). */
  trailing?: ReactNode;
  invalid?: boolean;
  /** Applies tabular figures — use for anything numeric. */
  numeric?: boolean;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    inputSize = 'md',
    icon: Icon,
    trailing,
    invalid,
    numeric = false,
    className,
    wrapperClassName,
    id,
    required,
    ...rest
  },
  ref,
) {
  const field = useFieldControl({
    id,
    required,
    'aria-invalid': invalid ? true : undefined,
    'aria-describedby': rest['aria-describedby'],
  });

  return (
    <div className={cn('relative flex items-center', wrapperClassName)}>
      {Icon ? (
        <Icon
          aria-hidden="true"
          className="pointer-events-none absolute left-3 size-4 shrink-0 text-text-faint"
        />
      ) : null}
      <input
        ref={ref}
        {...rest}
        {...field}
        className={cn(
          CONTROL_BASE,
          CONTROL_SIZE[inputSize],
          'px-3',
          Icon && 'pl-9',
          trailing && 'pr-11',
          numeric && 'num',
          className,
        )}
      />
      {trailing ? (
        <div className="absolute right-1 flex items-center gap-1 text-text-muted">{trailing}</div>
      ) : null}
    </div>
  );
});
